/**
 * Label / reason overrides, resolved per PROCEDURE CODE.
 *
 * ═══ WHY ═══
 *
 * A TC renaming D2740 "Crown" → "Cap" expects every crown on the plan to say
 * "Cap". Overrides used to be stored per PROCEDURE (annotations[procNum]) while
 * the AI-generated content they mask was keyed per CODE — so the rename applied
 * to one tooth and the TC had to repeat it for #23, #13 and #20, each showing
 * its own "overridden" badge. Overrides are now per code, matching the AI cache.
 *
 * This deliberately reverses an earlier documented decision (tx-presenter-comments.js
 * once noted per-procedure keying was intentional "so per-procedure overrides win
 * even when two procedures share a CodeNum"). Per-tooth overrides no longer exist.
 *
 * ═══ TWIN ═══
 *
 * This file has a twin in the teamio-serverless-api (tx-presenter-service/lib/resolveProcContent.js) repo. The block between the SHARED BODY
 * markers is BYTE-IDENTICAL in both and pinned by a parity test — only this
 * header and the export statement differ. Six hand-written copies of this
 * resolution chain had already drifted apart across the two repos; do not add a
 * seventh. Edit one twin, copy the body to the other, run the parity test.
 */

// ── SHARED BODY START (byte-identical FE/BE — pinned by
//    tx-presenter-service/tests/resolve-content-twin-parity.test.js) ──

/**
 * Collapse a plan's label/reason overrides to CODE level.
 *
 * Priority:
 *   1. annotations.codeContent[codeNum]  — the storage model since 2026-08-04
 *   2. legacy annotations[procNum].shortNameOverride / .reasonOverride, with the
 *      LOWEST procNum bearing that code winning
 *
 * (2) is a read-time shim, not a migration. The Annotations blob holds procNums
 * but no procNum→codeNum mapping — that lives in each site's Open Dental clinic
 * DB — so a bulk migration would have to query every site's DB per row and would
 * race live edits with no concurrency guard. The shim is deterministic and free;
 * legacy data self-migrates plan-by-plan as applyCodeOverride strips it on the
 * first edit.
 *
 * `procedures` should be the WIDEST list available (including alts and hidden
 * ones) so the legacy pick is stable no matter which surface resolves it.
 */
function extractCodeOverrides(annotations, procedures) {
  const anns = annotations || {};
  const out = { labels: {}, reasons: {} };
  const sorted = [...(procedures || [])]
    .filter((p) => p && p.procNum != null)
    .sort((a, b) => a.procNum - b.procNum);
  sorted.forEach((p) => {
    const a = anns[p.procNum];
    if (!a) return;
    const c = String(p.codeNum);
    if (a.shortNameOverride && out.labels[c] === undefined) out.labels[c] = a.shortNameOverride;
    if (a.reasonOverride && out.reasons[c] === undefined) out.reasons[c] = a.reasonOverride;
  });
  const cc = anns.codeContent || {};
  Object.keys(cc).forEach((c) => {
    const e = cc[c];
    if (!e) return;
    if (e.shortNameOverride) out.labels[String(c)] = e.shortNameOverride;
    if (e.reasonOverride) out.reasons[String(c)] = e.reasonOverride;
  });
  return out;
}

// AI cache values arrive as `{ content }` objects in the Builder store and as
// plain strings in the patient payload and backend cache reads. Accept both.
function aiText(v) {
  return typeof v === 'string' ? v : ((v && v.content) || '');
}

/** override(code) → AI(code) → abbrDesc → descript → procCode */
function resolveProcLabel(proc, codeOverrides, aiLabels) {
  if (!proc) return '';
  const ov = codeOverrides && codeOverrides.labels && codeOverrides.labels[String(proc.codeNum)];
  if (ov) return ov;
  const ai = aiText(aiLabels && aiLabels[proc.codeNum]);
  if (ai) return ai;
  return proc.abbrDesc || proc.descript || proc.procCode || '—';
}

/** override(code) → AI(code) → '' (a reason has no raw OD fallback) */
function resolveProcReason(proc, codeOverrides, aiReasons) {
  if (!proc) return '';
  const ov = codeOverrides && codeOverrides.reasons && codeOverrides.reasons[String(proc.codeNum)];
  if (ov) return ov;
  return aiText(aiReasons && aiReasons[proc.codeNum]) || '';
}

/**
 * Pure annotations transform for setting or clearing a code-level override.
 *
 * Also strips the matching legacy per-procNum keys for that code —
 * migrate-on-write, and it is load-bearing: without it, CLEARING an override
 * would resurrect the old per-tooth text through the read-time shim above.
 *
 * Pass a null/empty `value` to clear.
 */
function applyCodeOverride(annotations, procedures, { codeNum, field, value }) {
  const next = JSON.parse(JSON.stringify(annotations || {}));
  const key = String(codeNum);
  const cc = next.codeContent || {};
  const entry = { ...(cc[key] || {}) };
  const trimmed = value != null && String(value).trim().length > 0 ? String(value).trim() : null;
  if (trimmed) entry[field] = trimmed; else delete entry[field];
  if (Object.keys(entry).length > 0) cc[key] = entry; else delete cc[key];
  if (Object.keys(cc).length > 0) next.codeContent = cc; else delete next.codeContent;
  (procedures || []).forEach((p) => {
    if (!p || String(p.codeNum) !== key) return;
    const a = next[p.procNum];
    if (a && a[field]) {
      delete a[field];
      if (Object.keys(a).length === 0) delete next[p.procNum];
    }
  });
  return next;
}

/**
 * Codes on this plan that carry MORE THAN ONE distinct legacy per-tooth value —
 * i.e. plans that deliberately said different things for the same code and will
 * collapse to one under per-code overrides. Surfaced in the Builder so the
 * change is visible rather than silent.
 */
function findLegacyConflicts(annotations, procedures) {
  const anns = annotations || {};
  const byCode = {};
  (procedures || []).forEach((p) => {
    if (!p) return;
    const a = anns[p.procNum];
    if (!a) return;
    const c = String(p.codeNum);
    byCode[c] = byCode[c] || { procCode: p.procCode, labels: new Set(), reasons: new Set() };
    if (a.shortNameOverride) byCode[c].labels.add(a.shortNameOverride);
    if (a.reasonOverride) byCode[c].reasons.add(a.reasonOverride);
  });
  return Object.keys(byCode)
    .filter((c) => byCode[c].labels.size > 1 || byCode[c].reasons.size > 1)
    .map((c) => ({ codeNum: c, procCode: byCode[c].procCode }));
}

// ── SHARED BODY END ──

export {
  extractCodeOverrides,
  aiText,
  resolveProcLabel,
  resolveProcReason,
  applyCodeOverride,
  findLegacyConflicts,
};
